/**
 * Source-typing helpers (§17): map any `SourceRef` to its display kind, icon,
 * chip label, and tone. Every citation chip, peek-panel header, and lineage node
 * describes its source through this one function, so a new source variant is
 * styled in exactly one place — and an UNKNOWN variant degrades gracefully to a
 * neutral "source" chip rather than throwing (acceptance: unknown variants
 * degrade gracefully).
 */

import {
	Calculator,
	Database,
	FileSpreadsheet,
	FileText,
	Globe,
	HelpCircle,
	type LucideIcon,
	Mail,
	MessageSquare,
} from "lucide-react";

import type { SourceRef } from "../../types/citationArtifact";
import {
	commsChipLabel,
	commsTitle,
	displaySystemName as humanizeSystem,
	recordAsOf,
	recordChipLabel,
	recordLocator,
} from "../../utils/sourceLabels";

/**
 * The distinct kinds the UI styles. `comms` splits into `email`/`slack` because
 * their peek styling differs (email header vs Slack bubble), but they share the
 * `comms` locator variant.
 */
export type SourceDisplayKind =
	| "excel"
	| "pdf"
	| "web"
	| "record"
	| "email"
	| "slack"
	| "derived"
	| "unknown";

export interface SourceDescription {
	kind: SourceDisplayKind;
	/** Human-readable source system, e.g. "DealCloud", "Slack", "Excel". */
	system: string;
	Icon: LucideIcon;
	/** Compact label for the inline chip. */
	chipLabel: string;
	/** Longer text for the `title` tooltip / peek header subtitle. */
	title: string;
	/** Tailwind classes for the chip's border/text/background tone. */
	tone: string;
}

const TONES: Record<SourceDisplayKind, string> = {
	excel: "border-emerald-200 bg-emerald-50 text-emerald-700",
	pdf: "border-rose-200 bg-rose-50 text-rose-700",
	web: "border-sky-200 bg-sky-50 text-sky-700",
	record: "border-violet-200 bg-violet-50 text-violet-700",
	email: "border-amber-200 bg-amber-50 text-amber-700",
	slack: "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700",
	derived: "border-slate-200 bg-slate-50 text-slate-700",
	unknown: "border-gray-200 bg-gray-50 text-gray-600",
};

const ICONS: Record<SourceDisplayKind, LucideIcon> = {
	excel: FileSpreadsheet,
	pdf: FileText,
	web: Globe,
	record: Database,
	email: Mail,
	slack: MessageSquare,
	derived: Calculator,
	unknown: HelpCircle,
};

function shorten(text: string, max = 32): string {
	if (text.length <= max) return text;
	return `${text.slice(0, max - 1)}…`;
}

function describe(kind: SourceDisplayKind, partial: Partial<SourceDescription>): SourceDescription {
	return {
		kind,
		system: partial.system ?? "Source",
		Icon: ICONS[kind],
		chipLabel: partial.chipLabel ?? partial.system ?? "Source",
		title: partial.title ?? partial.chipLabel ?? partial.system ?? "Source",
		tone: TONES[kind],
	};
}

/**
 * Describe one source ref for typed rendering. Never throws: a ref whose `kind`
 * is not recognized falls through to the `unknown` description.
 */
export function describeSource(ref: SourceRef | null | undefined): SourceDescription {
	if (!ref || typeof ref !== "object") {
		return describe("unknown", { system: "Source" });
	}

	switch (ref.kind) {
		case "excel": {
			const loc = [ref.sheet, ref.cell ?? ref.range].filter(Boolean).join("!");
			const file = ref.filename ?? "Workbook";
			return describe("excel", {
				system: "Excel",
				chipLabel: loc || file,
				title: `${file}${loc ? ` — ${loc}` : ""}`,
			});
		}
		case "pdf": {
			const file = ref.filename ?? "Document";
			return describe("pdf", {
				system: "PDF",
				chipLabel: `${shorten(file, 22)} · p.${ref.page}`,
				title: `${file} — page ${ref.page}`,
			});
		}
		case "web": {
			let host = ref.title ?? ref.url;
			try {
				host = ref.title ?? new URL(ref.url).hostname;
			} catch {
				/* keep raw url */
			}
			return describe("web", {
				system: ref.publisher ?? "Web",
				chipLabel: shorten(host, 28),
				title: ref.title ? `${ref.title} — ${ref.url}` : ref.url,
			});
		}
		case "record": {
			// The locator is what an analyst re-queries by, so it is the chip
			// itself — not the bare object type it used to show. The as-of stamp
			// rides in the tooltip: it qualifies the value, but a chip has no room
			// for it. (Click-through is deferred to v2 — there is no record-card
			// endpoint to open.)
			const system = humanizeSystem(ref.source_system);
			return describe("record", {
				system,
				chipLabel: shorten(recordChipLabel(ref), 44),
				title: [system, recordLocator(ref), recordAsOf(ref)]
					.filter(Boolean)
					.join(" · "),
			});
		}
		case "comms": {
			// Named by conversation, not by sender: "which thread is this?" is the
			// question a reader asks of a comms citation, and the sender was the
			// only answer the chip used to give.
			const isSlack = ref.source_system === "slack";
			const kind: SourceDisplayKind = isSlack ? "slack" : "email";
			return describe(kind, {
				system: isSlack ? "Slack" : "Email",
				chipLabel: shorten(commsChipLabel(ref), 40),
				title: commsTitle(ref),
			});
		}
		case "derived": {
			return describe("derived", {
				system: "Computed",
				chipLabel: `Computed${ref.operation && ref.operation !== "other" ? ` · ${ref.operation}` : ""}`,
				title: ref.expression ?? ref.formula ?? "Derived value",
			});
		}
		default: {
			// Forward-compatible: an unrecognized variant still renders a chip.
			const raw = (ref as { kind?: unknown }).kind;
			return describe("unknown", {
				system: typeof raw === "string" ? humanizeSystem(raw) : "Source",
			});
		}
	}
}

/** Map a `SourceRef.kind` alone (no full ref) to its icon — for compact lists. */
export function iconForRefKind(kind: string | undefined): LucideIcon {
	switch (kind) {
		case "excel":
			return ICONS.excel;
		case "pdf":
			return ICONS.pdf;
		case "web":
			return ICONS.web;
		case "record":
			return ICONS.record;
		case "comms":
			return ICONS.email;
		case "derived":
			return ICONS.derived;
		default:
			return ICONS.unknown;
	}
}
